import { notify } from '@billy/design-system'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { NotificationKeys } from '../../../../../../../enums/notificationKeys'
import { useUpdateInvoicingSettings } from '../../../../../writer'
import { getCachedPaymentMethodsPayload } from '../../../utils/getCachedPaymentMethodsPayload'
import { PaymentMethodsForm } from '../types/PaymentMethodsForm'

interface UseUpdatePaymentMethodsProps {
  onSuccess?: () => void
}

export const useUpdatePaymentMethods = (props?: UseUpdatePaymentMethodsProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { onSuccess } = props || {}

  // The writer seeds the settings cache from the PUT response, so the editor's payment list updates without a refetch.
  const { update: updateSettings } = useUpdateInvoicingSettings()

  // Build the payload inside the mutation. An empty settings cache throws, and that throw has to
  // reach onError. Calling it while building the argument to mutate never does.
  const { mutate: update, isLoading: isProcessing } = useMutation(
    (formData: PaymentMethodsForm) =>
      updateSettings({
        values: { paymentMethods: getCachedPaymentMethodsPayload(formData, queryClient) },
      }),
    {
      onError: () => {
        notify({
          id: NotificationKeys.ExternalInvoiceSettingsUpdate,
          message: t('error_saving'),
          variant: 'error',
        })
      },
      onSuccess: () => {
        notify({
          id: NotificationKeys.ExternalInvoiceSettingsUpdate,
          message: t('external_invoices.payment_methods_modal.changes_saved'),
          variant: 'success',
        })
        onSuccess?.()
      },
    },
  )

  return { update, isProcessing }
}
